import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useMemo } from 'react'
import { formatDateParam, parseDateParam } from '@/lib/dates'
import { getNextPeriod, getPreviousPeriod } from '@/lib/periods'
import type { PeriodConfig } from '@/types/periods'

export function usePeriodNavigation(periodConfig: PeriodConfig) {
    const { period } = useLocalSearchParams<{ period?: string }>()
    const router = useRouter()

    const periodDate = useMemo(() => parseDateParam(period) ?? new Date(), [period])

    const handlePrevious = useCallback(() => {
        const prev = getPreviousPeriod(periodDate, periodConfig)
        router.setParams({ period: formatDateParam(prev) })
    }, [periodDate, periodConfig, router])

    const handleNext = useCallback(() => {
        const next = getNextPeriod(periodDate, periodConfig)
        router.setParams({ period: formatDateParam(next) })
    }, [periodDate, periodConfig, router])

    const handleToday = useCallback(() => {
        router.setParams({ period: undefined as unknown as string })
    }, [router])

    const handleDatePicked = useCallback(
        (date: Date) => {
            router.setParams({ period: formatDateParam(date) })
        },
        [router]
    )

    const isCurrentPeriod = periodDate.toDateString() === new Date().toDateString()

    return {
        periodDate,
        handlePrevious,
        handleNext,
        handleToday,
        handleDatePicked,
        isCurrentPeriod,
    }
}
